import { AssetsState } from './types';
import {
    ASSET_CLEAR_DEVICE_CACHE,
    ASSET_DELETION_FAILED,
    ASSET_DELETION_FINISHED,
    ASSET_DELETION_MODAL_CLOSE,
    ASSET_DELETION_MODAL_OPEN,
    ASSET_DELETION_RESET_STATE,
    ASSET_DELETION_STARTED,
    ASSET_DELETION_SUCCESSFUL,
    ASSET_TAB_CHANGED,
    ASSET_TAG_ADDING_FAILED,
    ASSET_TAG_ADDING_SUCCESSFUL,
    ASSET_TAG_REMOVING_FAILED,
    ASSET_TAG_REMOVING_SUCCESSFUL,
    ASSET_TAGS_UPDATING_FINISHED,
    ASSET_TAGS_UPDATING_RESET_STATE,
    ASSET_TAGS_UPDATING_STARTED,
    ASSET_UPDATE_RESET_STATE,
    ASSET_UPDATING_FAILED,
    ASSET_UPDATING_FINISHED,
    ASSET_UPDATING_STARTED,
    ASSET_UPDATING_SUCCESSFUL,
    ASSETS_APPLY_FILTER_CHANGES,
    ASSETS_LOADED,
    ASSETS_LOADED_FAILED,
    ASSETS_SELECTED_ASSOCIATED_DEVICES_CHANGED,
    ASSETS_SELECTED_DEVICES_LOADED,
    ASSETS_SELECTED_DEVICES_LOADED_FAILED,
    ASSETS_START_LOADING,
    ASSETS_START_LOADING_SELECTED_DEVICES,
    ASSETS_TABLE_ROW_SELECTED,
    ASSETS_TABLE_SEARCH_CHANGES,
    ASSETS_TABLE_SORT_CHANGES,
    ASSETS_TABLE_VIEW_TYPE_CHANGED,
    AssetsActions,
    ASSOCIATION_DELETION_SUCCESSFUL,
    ASSOCIATIONS_LOADED,
    ASSOCIATIONS_LOADED_FAILED,
    EASY_ONBOARDING_DIALOG_CLOSE,
    EASY_ONBOARDING_DIALOG_OPEN,
    FILTER_DIALOG_CLOSE,
    FILTER_DIALOG_OPEN,
    IS_ASSETS_RELOAD_IMMINENT,
    SELECTED_ASSET_UPDATE,
    SELECTED_MASTERDATA_UPDATE,
} from '../../actions/assets/types';
import TableViewToggles from '@rio-cloud/rio-uikit/lib/es/TableViewToggles';
import { AssociatedDevice, IdentificationType, SortDirection } from '../../components/assets/types';
import omit from 'lodash/omit';
import { TAG_CREATION_FAILED, TAG_CREATION_SUCCESSFUL } from '../../actions/tags/types';
import { AssetsListDetailTab } from '../../containers/assets/details/types';
import { AssetAssociationWithDevice } from '../../components/assets/details/types';
import { pushToObjectMapArray } from '../reducerHelper';

export const INITIAL_STATE: AssetsState = {
    assets: [],
    fetching: false,
    tableViewType: TableViewToggles.VIEW_TYPE_TABLE,
    selectedAsset: null,
    selectedAssociatedDevices: [],
    selectedDevicesFetching: false,
    associatedDevices: {},
    assetDeleting: false,
    displayDeleteDialog: false,
    deletionSuccessful: false,
    deletionErrorMessageCode: null,
    assetUpdating: false,
    assetUpdateSuccessful: false,
    assetUpdateFailed: false,
    assetUpdateErrorCode: null,
    searchString: '',
    filters: {
        showOnlyAssetsWithoutDataSource: false,
        showActiveAssets: true,
        showArchivedAssets: true,
    },
    showFilterOptionsDialog: false,
    sortBy: null,
    sortDir: SortDirection.ASCENDING,
    tags: {
        updating: false,
        removals: {
            lastSuccess: null,
            lastFailure: null,
        },
        additions: {
            lastSuccess: null,
            lastFailure: null,
        },
    },
    easyOnboardingDialog: { show: false, selectedAssetId: undefined },
    isAssetsReloadImminent: false,
    currentAssetDetailsTab: AssetsListDetailTab.ASSET_DETAILS,
};

function getAssetAssociatedDeviceMap(assetAssociationWithDevice: AssetAssociationWithDevice[]): {
    [key: string]: AssociatedDevice[];
} {
    const deviceMap: { [key: string]: AssociatedDevice[] } = {};
    assetAssociationWithDevice.forEach((association) => {
        pushToObjectMapArray(deviceMap, association.asset_id, {
            device: association['_embedded'].device,
            associationId: association.id,
        });
    });
    return deviceMap;
}

export default function reducer(state: AssetsState = INITIAL_STATE, action: AssetsActions): AssetsState {
    switch (action.type) {
        case ASSETS_START_LOADING:
            return {
                ...state,
                fetching: true,
                assets: [],
            };
        case ASSETS_LOADED:
            return {
                ...state,
                fetching: false,
                assets: action.payload,
            };
        case ASSETS_LOADED_FAILED:
            return {
                ...state,
                fetching: false,
                assets: [],
            };
        case ASSETS_TABLE_VIEW_TYPE_CHANGED:
            return {
                ...state,
                tableViewType: action.payload,
            };
        case ASSETS_TABLE_ROW_SELECTED: {
            const selectedAsset = action.payload;
            return {
                ...state,
                selectedAsset: selectedAsset?.id === state.selectedAsset?.id ? null : selectedAsset,
            };
        }
        case ASSETS_TABLE_SORT_CHANGES: {
            const sortBy = action.payload.sortBy;
            const sortDir = action.payload.sortDir;
            return {
                ...state,
                sortBy,
                sortDir,
            };
        }
        case ASSETS_TABLE_SEARCH_CHANGES:
            return {
                ...state,
                searchString: action.payload.searchString,
            };
        case ASSETS_APPLY_FILTER_CHANGES:
            return {
                ...state,
                filters: {
                    showOnlyAssetsWithoutDataSource: action.payload.showOnlyAssetsWithoutDataSources,
                    showActiveAssets: action.payload.showActiveAssets,
                    showArchivedAssets: action.payload.showArchivedAssets,
                },
            };
        case ASSETS_START_LOADING_SELECTED_DEVICES:
            return {
                ...state,
                selectedDevicesFetching: true,
                selectedAssociatedDevices: [],
            };
        case ASSETS_SELECTED_DEVICES_LOADED:
            return {
                ...state,
                selectedDevicesFetching: false,
                selectedAssociatedDevices: action.payload.associatedDevices,
                associatedDevices: {
                    ...state.associatedDevices,
                    [action.payload.assetId]: action.payload.associatedDevices,
                },
            };
        case ASSETS_SELECTED_ASSOCIATED_DEVICES_CHANGED:
            return {
                ...state,
                selectedDevicesFetching: false,
                selectedAssociatedDevices: action.payload.selectedAssociatedDevices,
            };
        case ASSETS_SELECTED_DEVICES_LOADED_FAILED:
            return {
                ...state,
                selectedDevicesFetching: false,
                selectedAssociatedDevices: [],
            };
        case ASSOCIATIONS_LOADED:
            return {
                ...state,
                associatedDevices: {
                    ...getAssetAssociatedDeviceMap(action.payload),
                },
                isAssetsReloadImminent: false,
            };
        case ASSOCIATIONS_LOADED_FAILED:
            return {
                ...state,
                isAssetsReloadImminent: false,
            };
        case ASSET_UPDATING_STARTED:
            return {
                ...state,
                assetUpdating: true,
            };
        case ASSET_UPDATING_SUCCESSFUL:
            return {
                ...state,
                assetUpdateSuccessful: true,
                assetUpdateFailed: false,
            };
        case ASSET_UPDATING_FAILED:
            return {
                ...state,
                assetUpdateSuccessful: false,
                assetUpdateFailed: true,
                assetUpdateErrorCode: action.payload.errorCode,
            };
        case ASSET_UPDATING_FINISHED:
            return {
                ...state,
                assetUpdating: false,
            };
        case ASSET_UPDATE_RESET_STATE:
            return {
                ...state,
                assetUpdating: false,
                assetUpdateSuccessful: false,
                assetUpdateFailed: false,
            };
        case SELECTED_ASSET_UPDATE:
            return {
                ...state,
                assets: state.assets.map((stateAsset) => {
                    if (stateAsset.id === action.payload.assetId) {
                        return {
                            ...stateAsset,
                            name: action.payload.name,
                            license_plate: action.payload.license_plate,
                            license_plate_country_code: action.payload.license_plate_country_code,
                            status: action.payload.status,
                            brand: action.payload.brand,
                            identification_type:
                                action.payload.identification_type === 'vin' ? IdentificationType.vin : null,
                            identification: action.payload.identification,
                        };
                    }

                    return stateAsset;
                }),
            };
        case SELECTED_MASTERDATA_UPDATE:
            return {
                ...state,
                assets: state.assets.map((stateAsset) => {
                    if (stateAsset.id === action.payload.assetId) {
                        return {
                            ...stateAsset,
                            masterdata: action.payload.masterdata,
                        };
                    }
                    return stateAsset;
                }),
            };
        case ASSET_DELETION_MODAL_OPEN:
            return {
                ...state,
                displayDeleteDialog: true,
            };
        case ASSET_DELETION_MODAL_CLOSE:
            return {
                ...state,
                displayDeleteDialog: false,
                deletionErrorMessageCode: null,
            };
        case ASSET_DELETION_STARTED:
            return {
                ...state,
                assetDeleting: true,
            };
        case ASSET_DELETION_SUCCESSFUL:
            return {
                ...state,
                deletionSuccessful: true,
                displayDeleteDialog: false,
                deletionErrorMessageCode: null,
                assets: state.assets.filter((stateAsset) => stateAsset.id !== action.payload.assetId),
            };
        case ASSET_DELETION_FAILED:
            return {
                ...state,
                deletionErrorMessageCode: 'DEFAULT',
            };
        case ASSET_DELETION_FINISHED:
            return {
                ...state,
                assetDeleting: false,
            };
        case ASSET_DELETION_RESET_STATE:
            return {
                ...state,
                assetDeleting: false,
                deletionSuccessful: false,
            };
        case ASSOCIATION_DELETION_SUCCESSFUL:
            return {
                ...state,
                selectedAssociatedDevices: state.selectedAssociatedDevices.filter(
                    (associatedDevice) => associatedDevice.associationId !== action.payload.associationId
                ),
                associatedDevices: Object.fromEntries(
                    Object.entries(state.associatedDevices).map(([assetId, associatedDevices]) => {
                        return [
                            assetId,
                            associatedDevices.filter(
                                (associatedDevice) => associatedDevice.associationId !== action.payload.associationId
                            ),
                        ];
                    })
                ),
            };
        case EASY_ONBOARDING_DIALOG_CLOSE:
            return {
                ...state,
                easyOnboardingDialog: {
                    show: false,
                    selectedAssetId: undefined,
                },
            };
        case EASY_ONBOARDING_DIALOG_OPEN:
            return {
                ...state,
                easyOnboardingDialog: {
                    show: true,
                    selectedAssetId: action.payload.selectedAssetId,
                },
            };
        case IS_ASSETS_RELOAD_IMMINENT:
            return { ...state, isAssetsReloadImminent: action.payload };
        case ASSET_CLEAR_DEVICE_CACHE: {
            const selectedDevices = state.selectedAsset?.id === action.payload ? [] : state.selectedAssociatedDevices;
            const devices = omit(state.associatedDevices, [action.payload]);
            return {
                ...state,
                selectedAssociatedDevices: selectedDevices,
                associatedDevices: devices,
            };
        }
        case ASSET_TAGS_UPDATING_STARTED:
            return {
                ...state,
                tags: {
                    updating: true,
                    removals: {
                        lastSuccess: null,
                        lastFailure: null,
                    },
                    additions: {
                        lastSuccess: null,
                        lastFailure: null,
                    },
                },
            };
        case ASSET_TAGS_UPDATING_FINISHED:
            return {
                ...state,
                tags: {
                    updating: false,
                    removals: {
                        ...state.tags.removals,
                    },
                    additions: {
                        ...state.tags.additions,
                    },
                },
            };
        case ASSET_TAGS_UPDATING_RESET_STATE:
            return {
                ...state,
                tags: {
                    updating: false,
                    removals: {
                        lastSuccess: null,
                        lastFailure: null,
                    },
                    additions: {
                        lastSuccess: null,
                        lastFailure: null,
                    },
                },
            };
        case ASSET_TAG_REMOVING_SUCCESSFUL:
            return {
                ...state,
                tags: {
                    ...state.tags,
                    removals: {
                        lastSuccess: action.payload.tag,
                        lastFailure: null,
                    },
                },
                assets: state.assets.map((asset) => {
                    if (asset.id === action.payload.asset.id) {
                        return {
                            ...asset,
                            tags: asset.tags.filter((tagId) => tagId !== action.payload.tag.id),
                        };
                    }
                    return asset;
                }),
                selectedAsset: {
                    ...action.payload.asset,
                    tags: state.selectedAsset
                        ? state.selectedAsset.tags.filter((tag) => tag !== action.payload.tag.id)
                        : [],
                },
            };
        case ASSET_TAG_REMOVING_FAILED:
            return {
                ...state,
                tags: {
                    ...state.tags,
                    removals: {
                        lastSuccess: null,
                        lastFailure: action.payload.tag,
                    },
                },
            };
        case ASSET_TAG_ADDING_SUCCESSFUL:
            return {
                ...state,
                tags: {
                    ...state.tags,
                    additions: {
                        lastSuccess: action.payload.tag,
                        lastFailure: null,
                    },
                },
                assets: state.assets.map((asset) => {
                    if (asset.id === action.payload.asset.id) {
                        return {
                            ...asset,
                            tags: asset.tags.includes(action.payload.tag.id)
                                ? asset.tags
                                : [...asset.tags, action.payload.tag.id],
                        };
                    }
                    return asset;
                }),
                selectedAsset: {
                    ...action.payload.asset,
                    tags: state.selectedAsset
                        ? [...state.selectedAsset.tags, action.payload.tag.id]
                        : [action.payload.tag.id],
                },
            };
        case ASSET_TAG_ADDING_FAILED:
            return {
                ...state,
                assets: state.assets.map((asset) => {
                    if (asset.id === action.payload.asset.id) {
                        return {
                            ...asset,
                            tags: asset.tags.filter((tag) => tag !== action.payload.tag.id),
                        };
                    }
                    return asset;
                }),
                tags: {
                    ...state.tags,
                    additions: {
                        lastSuccess: null,
                        lastFailure: action.payload.tag,
                    },
                },
            };
        // workaround to display tag immediately after creation. ASSET_TAG_ADDING_SUCCESSFUL will be dispatched
        // later when the backend is able to add the tag to the asset
        case TAG_CREATION_SUCCESSFUL:
            return {
                ...state,
                assets: state.assets.map((asset) => {
                    if (asset.id === action.payload.asset?.id) {
                        return {
                            ...asset,
                            tags: asset.tags.includes(action.payload.tag.id)
                                ? asset.tags
                                : [...asset.tags, action.payload.tag.id],
                        };
                    }
                    return asset;
                }),
            };
        case TAG_CREATION_FAILED:
            return {
                ...state,
                tags: {
                    ...state.tags,
                    additions: {
                        lastSuccess: null,
                        lastFailure: action.payload.tag,
                    },
                },
            };
        case ASSET_TAB_CHANGED:
            return {
                ...state,
                currentAssetDetailsTab: action.payload.selectedTab,
            };
        case FILTER_DIALOG_CLOSE:
            return {
                ...state,
                showFilterOptionsDialog: false,
            };
        case FILTER_DIALOG_OPEN:
            return {
                ...state,
                showFilterOptionsDialog: true,
            };
        default:
            return state;
    }
}
